import { useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

import { getImageUrl } from "../../../core/api";
import {
  getProduct,
  getProductFormMeta,
  saveProduct,
} from "../../../core/services/productService";
import AppImage from "../../../shared/AppImage";
import ToggleSwitch from "../../../shared/ToggleSwitch";

const MAX_IMAGES = 10;
const MAX_SIZE = 5 * 1024 * 1024;
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

type Category = {
  _id: string;
  name: string;
  isActive?: boolean;
};

type SubCategory = {
  _id: string;
  name: string;
  isActive?: boolean;
  category?: string | { _id?: string; name?: string };
};

type ProductForm = {
  name: string;
  isActive: boolean;
  shortDescription: string;
  description: string;
  category: string;
  subCategory: string;
  price: string;
  sellingPrice: string;
  stock: string;
  shippingCharge: string;
  isReturnable: boolean;
  isFeatured: boolean;
  isBestSeller: boolean;
  isNewArrival: boolean;
};

type ProductImage = {
  id: string;
  file: File;
  preview: string;
};

type VariantColor = {
  key: string;
  _id?: string;
  name: string;
  stock: string;
};

type ProductVariant = {
  key: string;
  _id?: string;
  name: string;
  price: string;
  compareAtPrice: string;
  stock: string;
  isActive: boolean;
  colors: VariantColor[];
};

const uid = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const blankColor = (stock = "0"): VariantColor => ({
  key: uid(),
  name: "",
  stock,
});

const colorStockTotal = (colors: VariantColor[]) =>
  colors.reduce((total, color) => total + (Number(color.stock) || 0), 0);

const blankVariant = (
  partial: Partial<ProductVariant> = {},
): ProductVariant => ({
  key: uid(),
  name: "",
  price: "",
  compareAtPrice: "",
  stock: "0",
  isActive: true,
  colors: [],
  ...partial,
});

const initialForm: ProductForm = {
  name: "",
  isActive: true,
  shortDescription: "",
  description: "",
  category: "",
  subCategory: "",
  price: "",
  sellingPrice: "",
  stock: "",
  shippingCharge: "",
  isReturnable: true,
  isFeatured: false,
  isBestSeller: false,
  isNewArrival: false,
};

const ProductAdd = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [form, setForm] = useState<ProductForm>(initialForm);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
  const [categoryId, setCategoryId] = useState("");
  const [images, setImages] = useState<ProductImage[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const imagesRef = useRef<ProductImage[]>([]);
  const [saving, setSaving] = useState(false);
  const [variants, setVariants] = useState<ProductVariant[]>([]);

  const discountPercent = useMemo(() => {
    const price = Number(form.price);
    const selling = Number(form.sellingPrice);

    return price > 0 && selling > 0 && selling < price
      ? Math.round(((price - selling) / price) * 100)
      : 0;
  }, [form.price, form.sellingPrice]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const meta = await getProductFormMeta();

        setCategories(
          (meta.categories || []).filter(
            (item: Category) => item.isActive !== false,
          ),
        );
        setSubCategories(
          (meta.subCategories || []).filter(
            (item: SubCategory) => item.isActive !== false,
          ),
        );
      } catch (error: any) {
        Swal.fire("Error", error.message, "error");
      }
    };

    loadInitialData();
  }, [isEditMode]);

  useEffect(() => {
    if (categoryId || !form.category || !categories.length) return;

    const matchingCategory = categories.find(
      (category) =>
        category.name.trim().toLowerCase() ===
        form.category.trim().toLowerCase(),
    );

    if (matchingCategory) {
      setCategoryId(matchingCategory._id);
    }
  }, [categories, categoryId, form.category]);

  useEffect(() => {
    if (!id) return;

    const fetchProductToEdit = async () => {
      try {
        const res = await getProduct(id);
        if (res.success && res.product) {
          const prod = res.product;
          setForm({
            name: prod.name || "",
            isActive: prod.isActive,
            shortDescription: prod.shortDescription || "",
            description: prod.description || "",
            category: prod.category?.name || prod.category || "",
            subCategory: prod.subCategory?.name || prod.subCategory || "",
            price: String(prod.price || ""),
            sellingPrice: String(prod.sellingPrice || ""),
            stock: String(prod.stock ?? ""),
            shippingCharge: String(prod.shippingCharge || ""),
            isReturnable: Boolean(prod.isReturnable),
            isFeatured: Boolean(prod.isFeatured),
            isBestSeller: Boolean(prod.isBestSeller),
            isNewArrival: Boolean(prod.isNewArrival),
          });
          setVariants(
            Array.isArray(prod.variants)
              ? prod.variants.map((variant: any) => {
                  return {
                    key: variant._id || uid(),
                    _id: variant._id,
                    name: variant.name || "",
                    price: String(variant.price ?? ""),
                    compareAtPrice:
                      Number(variant.compareAtPrice) > 0
                        ? String(variant.compareAtPrice)
                        : "",
                    stock: String(variant.stock ?? "0"),
                    isActive: variant.isActive !== false,
                    colors: Array.isArray(variant.colors)
                      ? variant.colors.map((color: any) => ({
                          key: color._id || uid(),
                          _id: color._id,
                          name: color.name || "",
                          stock: String(color.stock ?? "0"),
                        }))
                      : [],
                  };
                })
              : [],
          );

          setExistingImages(
            Array.isArray(prod.images)
              ? prod.images.filter(
                  (image: unknown): image is string =>
                    typeof image === "string" && Boolean(image),
                )
              : [],
          );

          if (prod.category?._id) {
            setCategoryId(prod.category._id);
          }
        }
      } catch (error: any) {
        Swal.fire("Error", error.message || "Failed to load product", "error");
      }
    };

    fetchProductToEdit();
  }, [id]);

  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  useEffect(
    () => () => {
      imagesRef.current.forEach((image) => URL.revokeObjectURL(image.preview));
    },
    [],
  );

  const update = <K extends keyof ProductForm>(
    key: K,
    value: ProductForm[K],
  ) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const changeCategory = (id: string) => {
    const category = categories.find((item) => item._id === id);

    setCategoryId(id);

    setForm((current) => ({
      ...current,
      category: category?.name || "",
      subCategory: "",
    }));
  };

  const availableSubCategories = useMemo(() => {
    if (!categoryId) return [];
    return subCategories.filter((item) => {
      const parentId =
        typeof item.category === "object" ? item.category?._id : item.category;
      return parentId === categoryId;
    });
  }, [categoryId, subCategories]);

  const selectImages = (files: FileList | null) => {
    if (!files?.length) return;

    const available = MAX_IMAGES - images.length - existingImages.length;
    if (available <= 0) {
      Swal.fire("Error", `Maximum ${MAX_IMAGES} images are allowed`, "error");
      return;
    }

    const selected = Array.from(files).slice(0, available);

    const validImages: ProductImage[] = [];

    selected.forEach((file) => {
      if (!IMAGE_TYPES.includes(file.type)) {
        Swal.fire("Error", `${file.name}: Invalid image type`, "error");
        return;
      }

      if (file.size > MAX_SIZE) {
        Swal.fire("Error", `${file.name}: Maximum size is 5MB`, "error");
        return;
      }

      const id = `${file.name}-${file.size}-${file.lastModified}`;

      if (images.some((image) => image.id === id)) return;

      validImages.push({
        id,
        file,
        preview: URL.createObjectURL(file),
      });
    });

    setImages((current) =>
      [...current, ...validImages].slice(
        0,
        Math.max(available, 0) + current.length,
      ),
    );
  };

  const removeImage = (id: string) => {
    setImages((current) => {
      const image = current.find((item) => item.id === id);

      if (image) URL.revokeObjectURL(image.preview);

      return current.filter((item) => item.id !== id);
    });
  };

  const removeExistingImage = (imageUrl: string) => {
    setExistingImages((current) =>
      current.filter((image) => image !== imageUrl),
    );
  };

  const priceDefaults = () => {
    const selling = form.sellingPrice || form.price || "";
    const mrp =
      form.price && Number(form.price) > Number(selling || 0) ? form.price : "";
    return { price: selling, compareAtPrice: mrp };
  };

  const validate = () => {
    const price = Number(form.price);
    const selling = Number(form.sellingPrice || 0);
    const stock = Number(form.stock);

    if (!form.category) return "Category is required";

    if (!variants.length) {
      if (!Number.isFinite(price) || price < 0) {
        return "Enter a valid price";
      }

      if (selling < 0 || selling > price) {
        return "Selling price cannot exceed price";
      }
    }

    if (
      !variants.length &&
      (!Number.isInteger(stock) || stock < 0 || stock > 9999)
    ) {
      return "Stock must be between 0 and 9999";
    }

    const variantNames = new Set<string>();
    for (const variant of variants) {
      const name = variant.name.trim();
      const normalized = name.toLowerCase();
      const variantPrice = Number(variant.price);
      const variantStock = Number(variant.stock);
      const mrp =
        variant.compareAtPrice === "" ? 0 : Number(variant.compareAtPrice);
      if (!name || variantNames.has(normalized)) {
        return "Each variant needs a unique name";
      }
      if (
        variant.price.trim() === "" ||
        !Number.isFinite(variantPrice) ||
        variantPrice < 0
      ) {
        return `Enter a valid selling price for ${name || "each variant"}`;
      }
      if (!Number.isFinite(mrp) || mrp < 0 || (mrp > 0 && mrp < variantPrice)) {
        return `${name} MRP cannot be less than its selling price`;
      }
      const colors = variant.colors || [];
      if (colors.length) {
        const colorNames = new Set<string>();
        for (const color of colors) {
          const colorName = color.name.trim();
          const colorKey = colorName.toLowerCase();
          const colorStock = Number(color.stock);
          if (!colorName || colorNames.has(colorKey)) {
            return `Each color on ${name} needs a unique name`;
          }
          if (
            color.stock.trim() === "" ||
            !Number.isInteger(colorStock) ||
            colorStock < 0 ||
            colorStock > 9999
          ) {
            return `Stock for ${name} ${colorName || "color"} must be between 0 and 9999`;
          }
          colorNames.add(colorKey);
        }
        if (colorStockTotal(colors) > 9999) {
          return `${name} stock cannot exceed 9999`;
        }
      } else if (
        variant.stock.trim() === "" ||
        !Number.isInteger(variantStock) ||
        variantStock < 0 ||
        variantStock > 9999
      ) {
        return `Stock for ${name} must be between 0 and 9999`;
      }
      variantNames.add(normalized);
    }
    if (variants.length && !variants.some((variant) => variant.isActive)) {
      return "Keep at least one variant active, or remove every variant";
    }
    const activeStock = variants
      .filter((variant) => variant.isActive)
      .reduce(
        (total, variant) =>
          total +
          (variant.colors.length
            ? colorStockTotal(variant.colors)
            : Number(variant.stock) || 0),
        0,
      );
    if (activeStock > 9999) {
      return "Combined variant stock cannot exceed 9999";
    }

    return "";
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const error = validate();

    if (error) {
      Swal.fire("Validation Error", error, "error");
      return;
    }

    setSaving(true);

    try {
      const payload = new FormData();

      Object.entries(form).forEach(([key, value]) => {
        payload.append(key, String(value));
      });
      payload.append(
        "variants",
        JSON.stringify(
          variants.map((variant) => ({
            ...(variant._id ? { _id: variant._id } : {}),
            name: variant.name.trim(),
            price: Number(variant.price),
            compareAtPrice:
              variant.compareAtPrice === ""
                ? 0
                : Number(variant.compareAtPrice),
            stock: variant.colors.length
              ? colorStockTotal(variant.colors)
              : Number(variant.stock),
            isActive: variant.isActive,
            colors: variant.colors.map((color) => ({
              ...(color._id ? { _id: color._id } : {}),
              name: color.name.trim(),
              stock: Number(color.stock),
            })),
          })),
        ),
      );

      images.forEach(({ file }) => {
        payload.append("images", file);
      });

      if (isEditMode) {
        payload.append("keepImages", JSON.stringify(existingImages));
      }

      const endpoint = isEditMode ? `/products/${id}` : "/products";
      const method = isEditMode ? "PUT" : "POST";

      const result = await saveProduct(endpoint, method, payload);

      if (!result.success) {
        throw new Error(
          result.message ||
            `Unable to ${isEditMode ? "update" : "create"} product`,
        );
      }

      await Swal.fire({
        title: isEditMode ? "Product Updated" : "Product Published",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });

      navigate("/products");
    } catch (error: any) {
      Swal.fire(
        isEditMode ? "Update Failed" : "Add Product Failed",
        error.message,
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="product-add-form" onSubmit={handleSubmit}>
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">MANAGEMENT</div>

          <h1 className="page-title">
            Product <em>{isEditMode ? "Edit" : "Add"}</em>
          </h1>

          <div className="page-sub">
            {isEditMode
              ? "Update product details and inventory."
              : "Create and publish a new product."}
          </div>
        </div>

        <div className="page-hdr-actions">
          <button
            type="submit"
            className="btn-sm btn-primary"
            disabled={saving}
          >
            {saving
              ? isEditMode
                ? "Updating..."
                : "Publishing..."
              : isEditMode
                ? "Update Product"
                : "Publish Product"}
          </button>
        </div>
      </div>

      <section className="add-grid">
        <section className="add-card">
          <h2>Product Information</h2>

          <div className="add-form-grid">
            <Field label="Product Name" required>
              <input
                required
                maxLength={100}
                value={form.name}
                onChange={(event) => update("name", event.target.value)}
                placeholder="Enter product name"
              />
            </Field>
            <Field label="Short Description">
              <input
                maxLength={180}
                value={form.shortDescription}
                onChange={(event) =>
                  update("shortDescription", event.target.value)
                }
                placeholder="Small product highlight"
              />
            </Field>

            <Field label="Category" required>
              <select
                required
                value={categoryId}
                onChange={(event) => changeCategory(event.target.value)}
              >
                <option value="">Select category</option>

                {categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Sub Category">
              <select
                value={form.subCategory}
                onChange={(event) => update("subCategory", event.target.value)}
                disabled={!categoryId}
              >
                <option value="">Select subcategory</option>
                {availableSubCategories.map((subCategory) => (
                  <option key={subCategory._id} value={subCategory.name}>
                    {subCategory.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Description" required className="span-3">
              <div className="editor-shell">
                <textarea
                  required
                  value={form.description}
                  onChange={(event) =>
                    update("description", event.target.value)
                  }
                  placeholder="Write product description..."
                />

                <small>
                  {form.description.split(/\s+/).filter(Boolean).length} words
                </small>
              </div>
            </Field>

            {variants.length ? (
              <Field label="Starting Price">
                <input
                  readOnly
                  value={(() => {
                    const prices = variants
                      .filter((variant) => variant.isActive)
                      .map((variant) => Number(variant.price))
                      .filter((value) => Number.isFinite(value));
                    return prices.length ? Math.min(...prices) : "";
                  })()}
                />
              </Field>
            ) : (
              <>
                <Field label="Price" required>
                  <input
                    required
                    type="number"
                    min="0"
                    max="99999999"
                    step="0.01"
                    value={form.price}
                    onChange={(event) => update("price", event.target.value)}
                    placeholder="0.00"
                  />
                </Field>

                <Field label="Selling Price">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.sellingPrice}
                    onChange={(event) =>
                      update("sellingPrice", event.target.value)
                    }
                    placeholder="0.00"
                  />
                </Field>

                <Field label="Discount Percent">
                  <input value={`${discountPercent}%`} readOnly />
                </Field>
              </>
            )}

            {variants.length ? (
              <Field label="Total Variant Stock">
                <input
                  readOnly
                  value={variants
                    .filter((variant) => variant.isActive)
                    .reduce(
                      (total, variant) => total + (Number(variant.stock) || 0),
                      0,
                    )}
                />
              </Field>
            ) : (
              <Field label="Stock Quantity" required>
                <input
                  required
                  type="number"
                  min="0"
                  max="9999"
                  value={form.stock}
                  onChange={(event) => update("stock", event.target.value)}
                  placeholder="0"
                />
              </Field>
            )}

            <Field label="Shipping Charge">
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.shippingCharge}
                onChange={(event) =>
                  update("shippingCharge", event.target.value)
                }
                placeholder="Leave blank to use default"
              />
            </Field>

            <Field label="Status">
              <ToggleSwitch
                label="Status"
                checked={form.isActive}
                onChange={(value) => update("isActive", value)}
              />
            </Field>

            <ToggleSwitch
              label="Featured Product"
              checked={form.isFeatured}
              onChange={(value) => update("isFeatured", value)}
            />

            <ToggleSwitch
              label="Best Seller"
              checked={form.isBestSeller}
              onChange={(value) => update("isBestSeller", value)}
            />

            <ToggleSwitch
              label="New Arrival"
              checked={form.isNewArrival}
              onChange={(value) => update("isNewArrival", value)}
            />

            <ToggleSwitch
              label="Returnable Product"
              checked={form.isReturnable}
              onChange={(value) => update("isReturnable", value)}
            />
          </div>
        </section>

        <section className="add-card product-variants-card">
          <div className="product-variants-heading">
            <div>
              <h2>Product Variants</h2>
              <p>
                Add each choice as its own variant. Each one has its own price
                and stock.
              </p>
            </div>
            <div className="product-variants-actions">
              <button
                type="button"
                className="btn-sm btn-outline"
                onClick={() =>
                  setVariants((current) => [
                    ...current,
                    blankVariant(priceDefaults()),
                  ])
                }
              >
                + Add variant
              </button>
              {variants.length > 0 && (
                <button
                  type="button"
                  className="btn-sm btn-outline"
                  onClick={() => setVariants([])}
                >
                  Clear variants
                </button>
              )}
            </div>
          </div>

          {variants.length ? (
            <div className="product-variants-list">
              {variants.map((variant, index) => {
                const updateVariant = (patch: Partial<ProductVariant>) =>
                  setVariants((current) =>
                    current.map((item) =>
                      item.key === variant.key ? { ...item, ...patch } : item,
                    ),
                  );
                return (
                  <article
                    className={`product-variant-card${variant.isActive ? "" : " is-inactive"}`}
                    key={variant.key}
                  >
                    <div className="product-variant-card-head">
                      <div className="product-variant-card-title">
                        <span>Variant {index + 1}</span>
                        <strong>
                          {variant.name.trim() || "Untitled variant"}
                        </strong>
                      </div>
                      <div className="product-variant-card-tools">
                        <ToggleSwitch
                          checked={variant.isActive}
                          checkedLabel="Active"
                          uncheckedLabel="Inactive"
                          onChange={(value) =>
                            updateVariant({ isActive: value })
                          }
                        />
                        <button
                          type="submit"
                          className="variant-save"
                          disabled={saving}
                        >
                          {saving ? "Saving..." : "Save"}
                        </button>
                        <button
                          type="button"
                          className="variant-delete"
                          aria-label={`Delete ${variant.name || "variant"}`}
                          onClick={() =>
                            setVariants((current) =>
                              current.filter(
                                (item) => item.key !== variant.key,
                              ),
                            )
                          }
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    <div className="product-variant-fields">
                      <Field label="Variant">
                        <input
                          value={variant.name}
                          onChange={(event) =>
                            updateVariant({ name: event.target.value })
                          }
                          placeholder="e.g. 500 g, Red, or Black / 128 GB"
                        />
                      </Field>
                      <Field label="MRP">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={variant.compareAtPrice}
                          onChange={(event) =>
                            updateVariant({
                              compareAtPrice: event.target.value,
                            })
                          }
                          placeholder="Optional"
                        />
                      </Field>
                      <Field label="Selling price">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={variant.price}
                          onChange={(event) =>
                            updateVariant({ price: event.target.value })
                          }
                          placeholder="0.00"
                        />
                      </Field>
                      <Field label="Stock">
                        <input
                          type="number"
                          min="0"
                          max="9999"
                          step="1"
                          value={
                            variant.colors.length
                              ? String(colorStockTotal(variant.colors))
                              : variant.stock
                          }
                          disabled={variant.colors.length > 0}
                          onChange={(event) =>
                            updateVariant({ stock: event.target.value })
                          }
                          placeholder="0"
                        />
                      </Field>
                    </div>
                    <div className="variant-colors">
                      <div className="variant-colors-head">
                        <span>Colors</span>
                        <button
                          type="button"
                          className="btn-sm btn-outline"
                          onClick={() =>
                            updateVariant({
                              colors: [
                                ...variant.colors,
                                blankColor(
                                  variant.colors.length
                                    ? "0"
                                    : variant.stock || "0",
                                ),
                              ],
                            })
                          }
                        >
                          + Add color
                        </button>
                      </div>
                      {variant.colors.map((color) => (
                        <div className="variant-color-row" key={color.key}>
                          <Field label="Color">
                            <input
                              value={color.name}
                              placeholder="Black"
                              onChange={(event) =>
                                updateVariant({
                                  colors: variant.colors.map((item) =>
                                    item.key === color.key
                                      ? { ...item, name: event.target.value }
                                      : item,
                                  ),
                                })
                              }
                            />
                          </Field>
                          <Field label="Color stock">
                            <input
                              type="number"
                              min="0"
                              max="9999"
                              step="1"
                              value={color.stock}
                              onChange={(event) =>
                                updateVariant({
                                  colors: variant.colors.map((item) =>
                                    item.key === color.key
                                      ? { ...item, stock: event.target.value }
                                      : item,
                                  ),
                                })
                              }
                            />
                          </Field>
                          <button
                            type="button"
                            className="variant-color-remove"
                            onClick={() =>
                              updateVariant({
                                colors: variant.colors.filter(
                                  (item) => item.key !== color.key,
                                ),
                              })
                            }
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <p className="table-muted">
              Add a variant when the same product has more than one choice.
            </p>
          )}
        </section>

        <section className="add-card">
          <h2>Product Images</h2>

          <div className="image-upload-grid">
            <label className="dropzone">
              <input
                type="file"
                multiple
                accept="image/png,image/jpeg,image/webp"
                onChange={(event) => {
                  selectImages(event.target.files);
                  event.target.value = "";
                }}
              />

              <strong>☁</strong>
              <span>Click to upload images</span>

              <small>
                JPG, PNG, WEBP up to 5MB.{" "}
                {images.length + existingImages.length}/{MAX_IMAGES}
              </small>
            </label>

            <div className="image-gallery">
              {existingImages.map((image, index) => (
                <div key={image}>
                  <AppImage
                    src={getImageUrl(image)}
                    alt={`${form.name || "Product"} ${index + 1}`}
                    crossOrigin="anonymous"
                  />

                  <button
                    type="button"
                    onClick={() => removeExistingImage(image)}
                  >
                    ×
                  </button>

                  {index === 0 && !images.length && <span>Cover</span>}
                </div>
              ))}

              {images.map((image, index) => (
                <div key={image.id}>
                  <img src={image.preview} alt={image.file.name} />

                  <button type="button" onClick={() => removeImage(image.id)}>
                    ×
                  </button>

                  {index === 0 && !existingImages.length && <span>Cover</span>}
                </div>
              ))}
            </div>
          </div>
        </section>
      </section>
    </form>
  );
};

const Field = ({
  label,
  required,
  className = "",
  children,
}: {
  label: string;
  required?: boolean;
  className?: string;
  children: ReactNode;
}) => (
  <label className={`add-field ${className}`}>
    <span>
      {label} {required && <b>*</b>}
    </span>

    {children}
  </label>
);

export default ProductAdd;
